import type {Page} from '@PosterWhiteboard/page/page.class';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';

export interface VisiblePageBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Returns the visible canvas bounds of the given page in page coordinates (unscaled pixels).
 *
 * Uses the page's .pmw-canvas-backdrop DOM element relative to the WHITEBOARD_CONTAINER viewport
 * to compute visible bounds — making the result page-relative regardless of scroll position or
 * how many pages precede the current one in a multi-page poster.
 *
 * Returns null when:
 * - Required DOM elements are not found
 * - The page is barely visible (< 10% of its height is showing), to avoid placing items
 *   at the extreme edges of the canvas
 */
export const getVisiblePageBounds = (page: Page): VisiblePageBounds | null => {
  const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get(0);
  if (!whiteboardContainer) return null;

  const whiteboardContainerRect = whiteboardContainer.getBoundingClientRect();
  const backDrop = document.querySelector<HTMLDivElement>(`.pmw-page-container[data-id="${page.hashedID}"] .pmw-canvas-backdrop`);
  if (!backDrop) return null;

  const backdropRect = backDrop.getBoundingClientRect();
  const scale = page.poster.scaling.scale;

  const visibleTop = Math.max(0, (whiteboardContainerRect.top - backdropRect.top) / scale);
  const visibleBottom = Math.min(page.poster.height, (whiteboardContainerRect.bottom - backdropRect.top) / scale);
  const visibleLeft = Math.max(0, (whiteboardContainerRect.left - backdropRect.left) / scale);
  const visibleRight = Math.min(page.poster.width, (whiteboardContainerRect.right - backdropRect.left) / scale);

  const visibleHeight = visibleBottom - visibleTop;
  if (visibleHeight < page.poster.height * 0.1) return null;

  return {top: visibleTop, bottom: visibleBottom, left: visibleLeft, right: visibleRight};
};
